import React, { lazy } from 'react';
import { Target, Zap, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
export function GuaranteesSection() {
  return <section className="py-24 bg-[#050505] relative overflow-hidden border-y border-gray-900" data-id="element-2476">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50 pointer-events-none" data-id="element-2477" />

      <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-2478">
        <div className="text-center mb-20" data-id="element-2479">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight" data-id="element-2480">
            Our <span className="text-[#FFD700]" data-id="element-2481">Guarantees</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-mono" data-id="element-2482">
            Protocol verified. Standards enforced. Peace of mind secured.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-2483">
          {/* Feature Image with Forensic Frame */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-2 md:order-1 relative group" data-id="element-2484">
            {/* Image Container */}
            <div className="relative p-2 border border-[#FF0000]/30 bg-[#0a0a0a]" data-id="element-2485">
              {/* Corner Markers */}
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-[#FF0000]" data-id="element-2486" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-[#FF0000]" data-id="element-2487" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2488" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2489" />

              <div className="relative overflow-hidden aspect-square flex items-center justify-center bg-[#050505]" data-id="element-2490">
                {/* Inline SVG Illustration */}
                <svg viewBox="0 0 200 200" className="w-full h-full p-8 opacity-80 group-hover:opacity-100 transition-opacity duration-700" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2491">
                  {/* Grid Background */}
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-2492">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#1F2937" strokeWidth="1" data-id="element-2493" />
                  </pattern>
                  <rect width="200" height="200" fill="url(#grid)" data-id="element-2494" />

                  {/* Shield Base */}
                  <path d="M100 20 L160 40 V90 C160 140 100 180 100 180 C100 180 40 140 40 90 V40 L100 20 Z" fill="#0A0A0A" stroke="#374151" strokeWidth="2" data-id="element-2495" />

                  {/* Inner Shield */}
                  <path d="M100 35 L145 50 V90 C145 130 100 160 100 160 C100 160 55 130 55 90 V50 L100 35 Z" fill="none" stroke="#FF0000" strokeWidth="2" strokeDasharray="4 4" className="animate-[spin_20s_linear_infinite]" style={{
                  transformOrigin: 'center'
                }} data-id="element-2496" />

                  {/* Lock Core */}
                  <rect x="85" y="90" width="30" height="25" rx="2" fill="#FFD700" stroke="#FFD700" strokeWidth="2" data-id="element-2497" />
                  <path d="M85 90 V80 C85 70 115 70 115 80 V90" fill="none" stroke="#FFD700" strokeWidth="3" strokeLinecap="round" data-id="element-2498" />
                  <circle cx="100" cy="102.5" r="3" fill="#0A0A0A" data-id="element-2499" />
                  <path d="M100 105.5 V110" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" data-id="element-2500" />

                  {/* Crosshairs */}
                  <path d="M100 0 V20 M100 180 V200 M0 100 H20 M180 100 H200" stroke="#FF0000" strokeWidth="1" opacity="0.5" data-id="element-2501" />
                  <circle cx="100" cy="100" r="80" stroke="#FF0000" strokeWidth="1" opacity="0.2" strokeDasharray="2 6" data-id="element-2502" />
                </svg>

                {/* Scan Line Overlay */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_15px_#FF0000] animate-[scan_4s_ease-in-out_infinite] opacity-50" data-id="element-2503" />

                {/* HUD Overlay Elements */}
                <div className="absolute top-4 left-4 flex gap-2" data-id="element-2504">
                  <span className="bg-black/70 border border-[#FF0000]/50 text-[#FF0000] text-[10px] font-mono px-2 py-1" data-id="element-2505">
                    SEC ●
                  </span>
                  <span className="bg-black/70 border border-gray-700 text-gray-300 text-[10px] font-mono px-2 py-1" data-id="element-2506">
                    AES-256
                  </span>
                </div>
              </div>
            </div>

            {/* Decorative Background Blob */}
            <div className="absolute -inset-4 bg-[#FF0000]/5 blur-2xl -z-10 rounded-full opacity-50" data-id="element-2507" />
          </motion.div>

          {/* Guarantees List */}
          <div className="space-y-6 order-1 md:order-2" data-id="element-2508">
            {/* Precision Technology */}
            <GuaranteeCard icon={<Target size={24} data-id="element-2510" />} title="Forensic Precision" description="Our algorithms are calibrated to detect subtext, slang, and context that general AI misses. We don't just read words; we read intent." delay={0.2} data-id="element-2509" />

            {/* Lightning Speed */}
            <GuaranteeCard icon={<Zap size={24} data-id="element-2512" />} title="Real-Time Processing" description="From upload to verdict in seconds. Anxiety doesn't wait, and neither should you. Get immediate clarity when you need it most." delay={0.4} data-id="element-2511" />

            {/* Accuracy */}
            <GuaranteeCard icon={<CheckCircle2 size={24} data-id="element-2514" />} title="99.9% Uptime Reliability" description="Always available for crisis moments. Our systems are redundant and monitored 24/7 to ensure we're there when you spiral." delay={0.6} data-id="element-2513" />
          </div>
        </div>
      </div>
    </section>;
}
const GuaranteeCard = ({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) => {
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay
  }} className="bg-[#0a0a0a] p-6 border-l-2 border-gray-800 hover:border-[#FF0000] transition-colors group relative" data-id="element-2515">
      <div className="flex items-start gap-5" data-id="element-2516">
        <div className="flex-shrink-0 w-12 h-12 bg-[#FF0000]/5 border border-[#FF0000]/20 flex items-center justify-center text-[#FF0000] group-hover:bg-[#FF0000] group-hover:text-white transition-all duration-300" data-id="element-2517">
          {icon}
        </div>
        <div className="flex-1 min-w-0" data-id="element-2518">
          <h3 className="font-bold text-xl text-white mb-2 group-hover:text-[#FF0000] transition-colors uppercase tracking-wide" data-id="element-2519">
            {title}
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed font-mono" data-id="element-2520">
            {description}
          </p>
        </div>
      </div>

      {/* Active Indicator */}
      <div className="absolute top-4 right-4 w-1.5 h-1.5 bg-[#FF0000] rounded-full opacity-0 group-hover:opacity-100 animate-pulse transition-opacity" data-id="element-2521" />
    </motion.div>;
};